import { convertThetaPhiToLatLng } from "@common/utils/UnitConverter";
import { UserRole } from "@live/api/user/UserRole";
import { Settings, SettingsItem } from "@live/settings/Settings";
import { increaseResolution } from "../universe/common/Coordinates";
import AirportSourceLayer from "./airport/AirportSourceLayer";
import FlightSourceLayer from "./flight/FlightSourceLayer";
import MapboxContext from "./MapboxContext";
export default class MapboxScene extends MapboxContext {
    pilots;
    ifatcs;
    mods;
    staffs;
    wizards;
    onClickFlights;
    airports;
    onClickAirports;
    constructor(containerId, mapboxToken) {
        super(containerId, mapboxToken);
        this.pilots = null;
        this.ifatcs = null;
        this.mods = null;
        this.staffs = null;
        this.wizards = null;
        this.onClickFlights = () => { };
        this.airports = null;
        this.onClickAirports = () => { };
    }
    setOnClickFlights(onClick) {
        this.onClickFlights = onClick;
    }
    setOnClickAirports(onClick) {
        this.onClickAirports = onClick;
    }
    targetFlight(flight) {
        const { lat, lng } = convertThetaPhiToLatLng(flight.theta, flight.phi);
        this.targetCamera(lat, lng);
    }
    async refreshFlights(flights) {
        this.stopAnimationLoop();
        const baseImageId = "flight-pilot-";
        const baseSourceLayerId = "flight-pilots-";
        const baseToFull = (base, n) => `${base}${n}`;
        const pilots = flights.filter((flight) => flight.role == UserRole.PILOT);
        const ifatcs = flights.filter((flight) => flight.role == UserRole.IFATC);
        const mods = flights.filter((flight) => flight.role == UserRole.MODERATOR);
        const staffs = flights.filter((flight) => flight.role == UserRole.INFINITE_FLIGHT_STAFF);
        const wizards = flights.filter((flight) => flight.role == UserRole.INFINITEX_DEVELOPER);
        this.refreshSourceLayer(this.pilots, baseToFull(baseImageId, 1), "img/flight_pilot.png", (imageId) => {
            this.pilots = new FlightSourceLayer(baseToFull(baseSourceLayerId, 1), imageId, pilots);
            this.pilots.setOnClick(this.onClickFlights);
            return this.pilots;
        });
        this.refreshSourceLayer(this.ifatcs, baseToFull(baseImageId, 2), "img/flight_ifatc.png", (imageId) => {
            this.ifatcs = new FlightSourceLayer(baseToFull(baseSourceLayerId, 2), imageId, ifatcs);
            this.ifatcs.setOnClick(this.onClickFlights);
            return this.ifatcs;
        });
        this.refreshSourceLayer(this.mods, baseToFull(baseImageId, 3), "img/flight_mod.png", (imageId) => {
            this.mods = new FlightSourceLayer(baseToFull(baseSourceLayerId, 3), imageId, mods);
            this.mods.setOnClick(this.onClickFlights);
            return this.mods;
        });
        this.refreshSourceLayer(this.staffs, baseToFull(baseImageId, 4), "img/flight_staff.png", (imageId) => {
            this.staffs = new FlightSourceLayer(baseToFull(baseSourceLayerId, 4), imageId, staffs);
            this.staffs.setOnClick(this.onClickFlights);
            return this.staffs;
        });
        this.refreshSourceLayer(this.wizards, baseToFull(baseImageId, 5), "img/flight_wizard.png", (imageId) => {
            this.wizards = new FlightSourceLayer(baseToFull(baseSourceLayerId, 5), imageId, wizards);
            this.wizards.setOnClick(this.onClickFlights);
            return this.wizards;
        });
        const updateLoop = () => {
            if (this.pilots == null ||
                this.ifatcs == null ||
                this.mods == null ||
                this.staffs == null ||
                this.wizards == null) {
                return;
            }
            const updateFlights = (flightSourceLayer) => {
                const features = flightSourceLayer.update();
                const source = this.getSource(flightSourceLayer.getId());
                source.setData({
                    "type": "FeatureCollection",
                    "features": features.map((feature) => feature.getValue())
                });
            };
            updateFlights(this.pilots);
            updateFlights(this.ifatcs);
            updateFlights(this.mods);
            updateFlights(this.staffs);
            updateFlights(this.wizards);
        };
        this.startAnimationLoop(updateLoop);
    }
    async refreshAirports(airports) {
        this.refreshSourceLayer(this.airports, "airport-active", "img/atc_active.png", (imageId) => {
            this.airports = new AirportSourceLayer("airport-actives", imageId, airports);
            this.airports.setOnClick(this.onClickAirports);
            return this.airports;
        });
    }
    async refreshRoute(flightDetails) {
        const str = Settings.get(SettingsItem.FLIGHT_ROUTE);
        const show = str == null || str != "false";
        if (!show) {
            return;
        }
        await this.holdUntilReady();
        const fplCoordinates = increaseResolution(flightDetails.fplCoordinates.map(({ theta, phi }) => { return { r: 1.0, theta, phi }; }), 100);
        this.addLine(fplCoordinates, "flight-fpl", "#D90429");
        this.addLine(flightDetails.route.map(({ theta, phi }) => { return { r: 1.0, theta, phi }; }), "flight-route", "#0197F6");
    }
    addLine(coordinates, id, hexColour) {
        const map = this.getMap();
        if (map.getLayer(id) != undefined) {
            map.removeLayer(id);
            map.removeSource(id);
        }
        const lngLats = coordinates.map(({ theta, phi }) => {
            const { lat, lng } = convertThetaPhiToLatLng(theta, phi);
            return [lng, lat];
        });
        for (let i = 0; i < lngLats.length - 1; i++) {
            const firstLng = lngLats[i][0];
            const secondLng = lngLats[i + 1][0];
            if (Math.abs(firstLng - secondLng) > 180) {
                if (secondLng - firstLng >= 180) {
                    lngLats[i + 1][0] -= 360;
                }
                else if (secondLng - firstLng < 180) {
                    lngLats[i + 1][0] += 360;
                }
            }
        }
        map.addSource(id, {
            'type': 'geojson',
            'data': {
                'type': 'Feature',
                'properties': {},
                'geometry': {
                    'type': 'LineString',
                    'coordinates': lngLats
                }
            }
        });
        map.addLayer({
            "id": id,
            "type": "line",
            "source": id,
            "layout": {
                "line-join": "round",
                "line-cap": "round"
            },
            "paint": {
                "line-color": hexColour,
                "line-width": 3,
            }
        });
    }
    async refreshSourceLayer(sourceLayer, imageId, imageSrc, onReseted) {
        await this.holdUntilReady();
        if (sourceLayer != null) {
            this.removeSourceLayer(sourceLayer);
        }
        if (!this.hasImage(imageId)) {
            const image = await this.loadImageBitmap(imageSrc);
            this.addImageBitmap(imageId, image);
        }
        const newSourceLayer = onReseted(imageId);
        this.addSourceLayer(newSourceLayer);
    }
}
